import { FormEvent, useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { CreditCard, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/lib/api'

// Shared by the public invoice and job payment pages.
export const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
)

export const readString = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return ''
}

export const normalizePublicImageUrl = (value: unknown) => {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return ''
  if (text.startsWith('http://') || text.startsWith('https://')) {
    return text
  }
  if (text.startsWith('/')) {
    return `${window.location.origin}${text}`
  }
  return ''
}

export type StripeIntentState = {
  publishableKey: string
  clientSecret: string
  paymentReferenceId: string
  walletId?: string | number | null
}

export type StripeInvoicePaymentFormProps = {
  paymentReferenceId: string
  onPaymentSucceeded: (paymentReferenceId: string) => Promise<void>
}

const stripePromiseCache = new Map<string, Promise<Stripe | null>>()

export const getStripePromise = (publishableKey: string) => {
  const cached = stripePromiseCache.get(publishableKey)
  if (cached) {
    return cached
  }
  const promise = loadStripe(publishableKey)
  stripePromiseCache.set(publishableKey, promise)
  return promise
}

export function StripeInvoicePaymentForm({ paymentReferenceId, onPaymentSucceeded }: StripeInvoicePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!stripe || !elements) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      if (result.error) {
        const errorWithIntent = result.error as typeof result.error & {
          payment_intent?: { id?: string; status?: string }
        }
        const intentFromError = errorWithIntent.payment_intent
        if (result.error.code === 'payment_intent_unexpected_state' && intentFromError?.status === 'succeeded') {
          await onPaymentSucceeded(intentFromError.id || paymentReferenceId)
          return
        }
        toast.error(result.error.message || 'Payment was not completed.')
        return
      }

      const confirmedIntent = result.paymentIntent
      if (confirmedIntent?.status !== 'succeeded') {
        toast.info(`Payment status: ${confirmedIntent?.status || 'pending'}`)
        return
      }

      await onPaymentSucceeded(confirmedIntent.id || paymentReferenceId)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <PaymentElement />
      <Button className="w-full" size="lg" type="submit" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
        Pay now
      </Button>
    </form>
  )
}
